import React, { useState } from 'react'
import { Typography, Chip } from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrdersListItemStyles } from './order-list-item.styles'
import { baseURL } from '../../../api'
import { OrderListItem } from '../../../services/api/orders/models'

const buildImageUrl = (imageId: number) =>
  `${baseURL}listed-items/images/${imageId}/medium.jpg`

const formatDate = (dateStr: string, locale?: string) => {
  try {
    return new Date(dateStr).toLocaleDateString(locale || undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const formatDateLong = (dateStr: string, locale?: string) => {
  try {
    return new Date(dateStr).toLocaleDateString(locale || undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const DetailRow: React.FC<{
  label: string
  value: string | undefined
  classes: Record<string, string>
}> = ({ label, value, classes }) =>
  value ? (
    <div className={classes.orderCardDetailRow}>
      <span className={classes.orderCardDetailLabel}>{label}:</span>
      <span className={classes.orderCardDetailValue}>{value}</span>
    </div>
  ) : null

const getOrderImageSrc = (order: OrderListItem): string | null => {
  if (order.imageUrl) return order.imageUrl
  if (order.defaultImageId != null) return buildImageUrl(order.defaultImageId)
  return null
}

export const ORDER_LIST_PLACEHOLDER_COUNT = 4

export const OrdersListItemCardPlaceholder: React.FC = () => {
  const classes = useOrdersListItemStyles()
  return (
    <div
      className={`${classes.orderCard} ${classes.orderCardSkeleton}`}
      aria-hidden
    >
      <div className={classes.orderCardSummary}>
        <Skeleton
          variant="rect"
          className={classes.skeletonThumb}
          animation="wave"
        />
        <div className={classes.orderCardBody}>
          <Skeleton
            animation="wave"
            height={22}
            width="58%"
            style={{ marginBottom: 8 }}
          />
          <Skeleton
            animation="wave"
            height={18}
            width="42%"
            style={{ marginBottom: 8 }}
          />
          <Skeleton animation="wave" height={16} width="88%" />
        </div>
        <div className={classes.orderCardSummaryRight}>
          <Skeleton
            variant="rect"
            width={76}
            height={26}
            animation="wave"
            style={{ borderRadius: 13 }}
          />
          <Skeleton variant="circle" width={22} height={22} animation="wave" />
        </div>
      </div>
    </div>
  )
}

export const OrdersListItemCard: React.FC<{
  order: OrderListItem
  statusLabel: string
  /** Link to seller order page; omit or false for purchased orders */
  showViewOrderLink?: boolean
}> = ({ order, statusLabel, showViewOrderLink = false }) => {
  const classes = useOrdersListItemStyles()
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const imageSrc = getOrderImageSrc(order)
  const lng = i18n.language
  const titleText =
    order.itemName?.trim() ||
    t('ordersListPage.fallbackOrderTitle', { orderNumber: order.orderNumber })
  const imageAlt = order.itemName?.trim()
    ? t('ordersListPage.itemImageAltWithTitle', { title: order.itemName })
    : t('ordersListPage.itemImageAlt')
  const deliveryValue =
    order.deliveryMode != null
      ? t(`ordersListPage.deliveryOption.${order.deliveryMode}`)
      : order.deliveryType
  const currency = t('ordersListPage.currencySymbol')
  const hasDetails =
    order.deliveryMode ||
    order.deliveryType ||
    order.shippingProvider ||
    order.shippingName ||
    order.shippingAddress ||
    order.buyerName ||
    order.buyerEmail ||
    order.nonprofitName ||
    order.subtotalAmount != null ||
    order.shippingAmount != null

  const onDetailsToggle: React.ReactEventHandler<HTMLDetailsElement> = (e) => {
    setExpanded(e.currentTarget.open)
  }

  return (
    <div className={classes.orderCard}>
      <details
        className={classes.orderCardDisclosure}
        open={expanded}
        onToggle={onDetailsToggle}
      >
        <summary
          className={classes.orderCardSummary}
          aria-label={
            expanded
              ? t('ordersListPage.collapseOrderDetails')
              : t('ordersListPage.expandOrderDetails')
          }
        >
          {order.listedItemId != null ? (
            <Link
              to={`/listed-items/${order.listedItemId}`}
              className={`${classes.orderCardThumb} ${classes.orderCardThumbLink}`}
              onClick={(e) => e.stopPropagation()}
            >
              {imageSrc ? <img src={imageSrc} alt={imageAlt} /> : null}
            </Link>
          ) : (
            <div className={classes.orderCardThumb}>
              {imageSrc ? <img src={imageSrc} alt={imageAlt} /> : null}
            </div>
          )}
          <div className={classes.orderCardBody}>
            {order.listedItemId != null ? (
              <Link
                to={`/listed-items/${order.listedItemId}`}
                className={`${classes.orderCardTitle} ${classes.orderCardTitleLink}`}
                onClick={(e) => e.stopPropagation()}
              >
                {titleText}
              </Link>
            ) : (
              <Typography className={classes.orderCardTitle} variant="subtitle1">
                {titleText}
              </Typography>
            )}
            <Typography className={classes.orderCardMeta} variant="body2">
              {t('ordersListPage.orderMetaLine', {
                orderNumber: order.orderNumber,
                date: formatDate(order.orderCreatedUtc, lng),
              })}
            </Typography>
            {showViewOrderLink && (
              <Link
                to={`/selling/order/${order.orderId}`}
                className={classes.orderCardViewOrderLink}
                onClick={(e) => e.stopPropagation()}
              >
                {t('ordersListPage.viewOrder') as string}
              </Link>
            )}
            {order.description && (
              <Typography
                className={classes.orderCardDescription}
                variant="body2"
              >
                {order.description}
              </Typography>
            )}
          </div>
          <div className={classes.orderCardSummaryRight}>
            <Chip label={statusLabel} color="primary" size="small" />
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </div>
        </summary>

        {expanded && hasDetails && (
          <div className={classes.orderCardExpanded}>
            <div className={classes.orderCardDetails}>
              <div className={classes.orderCardDetailsColumn}>
                {(order.deliveryMode ||
                  order.deliveryType ||
                  order.shippingProvider ||
                  order.shippingName ||
                  order.shippingAddress) && (
                  <div className={classes.orderCardSection}>
                    <Typography
                      className={classes.orderCardSectionTitle}
                      variant="subtitle2"
                    >
                      {t('ordersListPage.orderDetails')}
                    </Typography>
                    <DetailRow
                      label={t('ordersListPage.created')}
                      value={formatDateLong(order.orderCreatedUtc, lng)}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.orderNumber')}
                      value={order.orderNumber}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.delivery')}
                      value={deliveryValue}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.shippingProvider')}
                      value={order.shippingProvider}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.shippingRate')}
                      value={order.shippingRate}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.shippingName')}
                      value={order.shippingName}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.shippingAddress')}
                      value={order.shippingAddress}
                      classes={classes}
                    />
                  </div>
                )}
              </div>

              <div className={classes.orderCardDetailsColumn}>
                {(order.buyerName || order.buyerEmail) && (
                  <div className={classes.orderCardSection}>
                    <Typography
                      className={classes.orderCardSectionTitle}
                      variant="subtitle2"
                    >
                      {t('ordersListPage.buyer')}
                    </Typography>
                    <DetailRow
                      label={t('ordersListPage.name')}
                      value={order.buyerName}
                      classes={classes}
                    />
                    <DetailRow
                      label={t('ordersListPage.email')}
                      value={order.buyerEmail}
                      classes={classes}
                    />
                  </div>
                )}

                {order.nonprofitName && (
                  <div className={classes.orderCardSection}>
                    <DetailRow
                      label={t('ordersListPage.nonprofitName')}
                      value={order.nonprofitName}
                      classes={classes}
                    />
                  </div>
                )}

                {(order.subtotalAmount != null ||
                  order.shippingAmount != null ||
                  order.totalAmount != null) && (
                  <div className={classes.orderCardAmountBlock}>
                    <div className={classes.orderCardAmountRow}>
                      <span className={classes.orderCardDetailLabel}>
                        {t('ordersListPage.labelPrice')}
                      </span>
                      <span className={classes.orderCardDetailValue}>
                        {currency}
                        {(
                          order.subtotalAmount ??
                          (order.totalAmount ?? 0) - (order.shippingAmount ?? 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className={classes.orderCardAmountRow}>
                      <span className={classes.orderCardDetailLabel}>
                        {t('ordersListPage.labelShipping')}
                      </span>
                      <span className={classes.orderCardDetailValue}>
                        {currency}
                        {(order.shippingAmount ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div
                      className={`${classes.orderCardAmountRow} ${classes.orderCardAmountTotal}`}
                    >
                      <span>{t('ordersListPage.labelTotal')}</span>
                      <span>
                        {currency}
                        {(order.totalAmount ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </details>
    </div>
  )
}
